import { useEffect } from 'react'

import type { IOptionItem } from '~/client/src/shared/components/OptionsList/types'

import { ILocalDefaults } from './parseDefaultOptions'

export function useCleanStaleDefaults({
  propertiesReady,
  sortOptions,
  groupByOptions,
  filterCategories,
  defaults,
  onChange,
  skipGroupByKeys,
  extraValidFilterKeys,
  fallbackGroupByKey = null,
}: {
  propertiesReady: boolean
  sortOptions: IOptionItem[]
  groupByOptions: IOptionItem[]
  filterCategories: Array<{ id: string }>
  defaults: ILocalDefaults
  onChange: (cleaned: ILocalDefaults) => void
  skipGroupByKeys?: Set<string>
  extraValidFilterKeys?: Set<string>
  fallbackGroupByKey?: string | null
}) {
  useEffect(() => {
    if (!propertiesReady) return

    const validSortKeys = new Set(sortOptions.map(option => option.key))
    const validGroupByKeys = new Set(groupByOptions.map(option => option.key))
    const validFilterCategoryIds = new Set(
      filterCategories.map(category => category.id),
    )

    const sortKey =
      defaults.sortKey && !validSortKeys.has(defaults.sortKey)
        ? null
        : defaults.sortKey
    const groupByKey =
      defaults.groupByKey &&
      !validGroupByKeys.has(defaults.groupByKey) &&
      !skipGroupByKeys?.has(defaults.groupByKey)
        ? fallbackGroupByKey
        : defaults.groupByKey
    const filterSelection = defaults.filterSelection.filter(id => {
      const colonIndex = id.indexOf(':')
      if (colonIndex === -1) return false
      const facetKey = id.slice(0, colonIndex)
      return (
        validFilterCategoryIds.has(facetKey) ||
        !!extraValidFilterKeys?.has(facetKey)
      )
    })

    if (
      sortKey === defaults.sortKey &&
      groupByKey === defaults.groupByKey &&
      filterSelection.length === defaults.filterSelection.length
    ) {
      return
    }

    onChange({
      ...defaults,
      sortKey,
      groupByKey,
      filterSelection,
    })
  }, [
    propertiesReady,
    sortOptions,
    groupByOptions,
    filterCategories,
    defaults,
    onChange,
    skipGroupByKeys,
    extraValidFilterKeys,
    fallbackGroupByKey,
  ])
}
